import { Dialog } from "@mui/material";
import { Icon } from "@iconify/react";
import { HStack, VStack } from "../../../../components/utils";
import { useGetExerciseLettersAdmin } from "../../../../queries/transactionV2";
import type { ExerciseLetterAdminDoc } from "../../../../api/TransactionApisV2";

interface ExerciseLetterPreviewDialogProps {
  open: boolean;
  transactionId: string;
  employeeId: string;
  employeeName?: string;
  requestId?: string;
  onClose: () => void;
}

function LetterTile({ doc }: { doc: ExerciseLetterAdminDoc }) {
  return (
    <HStack className="items-center justify-between px-4 py-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
      <HStack className="items-center gap-3 min-w-0">
        <Icon
          icon="mdi:file-pdf-box"
          className="w-6 h-6 text-red-500 flex-shrink-0"
        />
        <VStack className="min-w-0">
          <span className="text-sm font-medium text-gray-800 truncate">
            {doc.fileName}
          </span>
          {doc.signedByEmployee && (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
              <Icon icon="mdi:check-circle" className="w-3.5 h-3.5" />
              Signed by employee
            </span>
          )}
        </VStack>
      </HStack>
      <a
        href={doc.s3Url}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1.5 text-xs font-medium text-orange-600 border border-orange-300 rounded px-3 py-1.5 hover:bg-orange-50 transition-colors shrink-0 ml-3"
      >
        <Icon icon="mdi:eye-outline" className="w-3.5 h-3.5" />
        Preview
      </a>
    </HStack>
  );
}

export default function ExerciseLetterPreviewDialog({
  open,
  transactionId,
  employeeId,
  employeeName,
  requestId,
  onClose,
}: ExerciseLetterPreviewDialogProps) {
  const { data: letters, isLoading } = useGetExerciseLettersAdmin(
    transactionId,
    employeeId,
    requestId
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <VStack className="p-6 min-w-[480px]">
        <HStack className="items-center justify-between mb-5">
          <VStack className="gap-0.5">
            <span className="text-base font-semibold text-gray-900">
              Exercise Letters
            </span>
            {employeeName && (
              <span className="text-sm text-gray-500">{employeeName}</span>
            )}
          </VStack>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          >
            <Icon icon="mdi:close" className="w-5 h-5 text-gray-500" />
          </button>
        </HStack>

        {isLoading ? (
          <div className="flex items-center justify-center py-10 gap-2 text-gray-500 text-sm">
            <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
            Loading letters…
          </div>
        ) : !letters || letters.length === 0 ? (
          <p className="text-sm text-gray-400 italic py-6 text-center">
            No exercise letters generated for this employee yet.
          </p>
        ) : (
          <VStack className="gap-2">
            {letters.map((doc) => (
              <LetterTile key={doc.id} doc={doc} />
            ))}
          </VStack>
        )}
      </VStack>
    </Dialog>
  );
}
